import { BAR_TICK_INTERVAL, DISPATCH_TICK_INTERVAL, UI_TICK_INTERVAL } from "./constants";
import type { EventRegistry } from "./EventRegistry";
import type { TransportLike } from "./tone";

export interface TransportHandlers {
  /** Receives Tone's callback time — the only reservation time origin. */
  onDispatchTick: (callbackTimeSec: number) => void;
  onBarTick: () => void;
  onUiTick: () => void;
}

/**
 * Owns the three permanent Transport callbacks and nothing else.
 *
 * Registration happens exactly once (in AudioEngine.init) and clearing happens
 * exactly once (in AudioEngine.dispose), by id. Transport.cancel() is never
 * used: it would also remove repeated events and desync EventRegistry.
 */
export class TransportController {
  constructor(
    private readonly transport: TransportLike,
    private readonly registry: EventRegistry,
  ) {}

  register(handlers: TransportHandlers): void {
    this.registry.assertEmpty();
    this.registry.register(
      this.transport.scheduleRepeat((timeSec) => {
        handlers.onDispatchTick(timeSec);
      }, DISPATCH_TICK_INTERVAL),
    );
    this.registry.register(
      this.transport.scheduleRepeat(() => {
        handlers.onBarTick();
      }, BAR_TICK_INTERVAL),
    );
    this.registry.register(
      this.transport.scheduleRepeat(() => {
        handlers.onUiTick();
      }, UI_TICK_INTERVAL),
    );
  }

  clearAll(): void {
    this.registry.clearAll((id) => {
      this.transport.clear(id);
    });
  }

  assertFull(): void {
    this.registry.assertFull();
  }

  get callbackCount(): number {
    return this.registry.size;
  }
}
